import type {
    QuestionComment,
    QuestionResultData,
    TextAnswer,
} from '@/components/QuestionResultContainer';
import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { QuestionType } from '@/constants/QuestionType';
import { ReviewType } from '@/constants/review/CycleInstance';
import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';
import { isNonCommentableQuestionType } from '@/utils/question';

interface GetAllReviewersParams {
    reviewInstance?: ReviewInstance;
    users: User[];
}

export const getAllReviewInstanceAssignedUserIds = (
    reviewInstances: ReviewInstance[],
): string[] => {
    const userIds: Set<string> = new Set();

    reviewInstances.forEach((reviewInstance) => {
        // Direct assignedTo on ReviewInstance
        if (reviewInstance.assignedTo) {
            userIds.add(reviewInstance.assignedTo);
        }

        // The manager running the assessment — the reviewer on a manager evaluation, and the only
        // place they are recorded before they answer anything. Without this, getAllReviewers below
        // has no user to match against.
        if (reviewInstance.cycleInstance?.assignedTo) {
            userIds.add(reviewInstance.cycleInstance.assignedTo);
        }

        // Nested in reviewAudienceAssignment (reviewee)
        const audienceAssignment = reviewInstance.reviewAudienceAssignment;
        if (audienceAssignment?.assignedTo) {
            userIds.add(audienceAssignment.assignedTo);
        }

        // Reviewers from reviewAnswers
        reviewInstance.reviewAnswers?.forEach((reviewAnswer) => {
            if (reviewAnswer.assignedTo) {
                userIds.add(reviewAnswer.assignedTo);
            }
        });
    });

    return Array.from(userIds);
};

export const getAllReviewers = ({ reviewInstance, users }: GetAllReviewersParams): User[] => {
    // On a Manager Evaluation the reviewer is the manager who runs the assessment — the cycle
    // instance's assignedTo. The ReviewInstance's own assignedTo is the assessed employee, so
    // reading it here listed the reviewee as their own reviewer. reviewAnswers is not usable
    // either: it stays empty until the manager answers something.
    if (reviewInstance?.cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION) {
        const managerId = reviewInstance.cycleInstance?.assignedTo;
        return managerId ? users.filter((user) => user.id === managerId) : [];
    }

    const reviewerIds =
        reviewInstance?.reviewAnswers?.map((reviewAnswer) => reviewAnswer.assignedTo) ?? [];

    return users.filter((user) => reviewerIds.includes(user.id));
};

export const getReviewee = ({ reviewInstance, users }: GetAllReviewersParams): User | undefined => {
    return reviewInstance?.reviewAudienceAssignment?.assignedTo
        ? users.find((user) => user.id === reviewInstance?.reviewAudienceAssignment?.assignedTo)
        : undefined;
};

export const getCurrentUser = ({
    reviewInstance,
    users,
}: GetAllReviewersParams): User | undefined => {
    return reviewInstance?.reviewAudienceAssignment?.assignedTo
        ? users.find((user) => user.id === reviewInstance?.reviewAudienceAssignment?.assignedTo)
        : undefined;
};

// A manager assessment is a single-evaluator, multi-step flow, so its progress is measured by the
// stepper (managerAssessmentStep of MANAGER_ASSESSMENT_TOTAL_STEPS) rather than answered evaluators.
const MANAGER_ASSESSMENT_TOTAL_STEPS = 4;
const MAX_PROGRESS_PERCENTAGE = 100;

export const getProgressPercentage = (reviewInstance: ReviewInstance): number => {
    if (reviewInstance.cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION) {
        if (reviewInstance.status === ReviewInstanceStatus.COMPLETED) {
            return MAX_PROGRESS_PERCENTAGE;
        }
        // managerAssessmentStep is the step the manager is currently on, so completed steps = step - 1.
        const step = reviewInstance.cycleInstance?.managerAssessmentStep ?? 1;
        const completedSteps = Math.max(step - 1, 0);
        return Math.min(
            (completedSteps / MANAGER_ASSESSMENT_TOTAL_STEPS) * MAX_PROGRESS_PERCENTAGE,
            MAX_PROGRESS_PERCENTAGE,
        );
    }

    if (reviewInstance.totalCount === 0) {
        return 0;
    }

    return (reviewInstance.responseCount / reviewInstance.totalCount) * MAX_PROGRESS_PERCENTAGE;
};

interface GetReviewQuestionAnswersParams {
    reviewInstance?: ReviewInstance;
    users: User[];
}

export const getReviewQuestionAnswers = ({
    reviewInstance,
    users,
}: GetReviewQuestionAnswersParams): QuestionResultData[] => {
    if (!reviewInstance?.reviewAnswers) {
        return [];
    }

    const questionMap = new Map<
        string,
        QuestionResultData & {
            answerData: Record<string | number, number>;
            textAnswers: TextAnswer[];
            comments: QuestionComment[];
        }
    >();

    for (const reviewAnswer of reviewInstance.reviewAnswers) {
        const reviewerUser = reviewAnswer.assignedTo
            ? (users.find((u) => u.id === reviewAnswer.assignedTo) ?? null)
            : null;

        for (const questionAnswer of reviewAnswer.reviewQuestionAnswers) {
            const questionId = questionAnswer.reviewQuestionId;
            const existing = questionMap.get(questionId);

            const aggregateAnswer = (answerData: Record<string | number, number>) => {
                const questionType = questionAnswer.reviewQuestion.questionType;
                const isRankingQuestion = questionType === QuestionType.RANKING;
                const isYesNoQuestion = questionType === QuestionType.YES_NO;

                if (
                    isYesNoQuestion &&
                    questionAnswer.boolValue !== undefined &&
                    questionAnswer.boolValue !== null
                ) {
                    const index = questionAnswer.boolValue ? 0 : 1;
                    answerData[index] = (answerData[index] ?? 0) + 1;
                } else if (questionAnswer.selectedOptions.length > 0) {
                    if (isRankingQuestion) {
                        const sortedSelections = [...questionAnswer.selectedOptions].sort(
                            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
                        );
                        const optionsCount = sortedSelections.length;

                        sortedSelections.forEach((selection, index) => {
                            const optionId = selection.reviewQuestionOptionId;
                            const score = optionsCount - index;
                            answerData[optionId] = (answerData[optionId] ?? 0) + score;
                        });
                    } else {
                        for (const selection of questionAnswer.selectedOptions) {
                            const optionId = selection.reviewQuestionOptionId;
                            answerData[optionId] = (answerData[optionId] ?? 0) + 1;
                        }
                    }
                } else if (
                    questionAnswer.intValue !== undefined &&
                    questionAnswer.intValue !== null
                ) {
                    answerData[questionAnswer.intValue] =
                        (answerData[questionAnswer.intValue] ?? 0) + 1;
                }
            };

            const replies: QuestionComment[] = (questionAnswer.commentReplies ?? []).map(
                (reply) => ({
                    id: reply.id,
                    reviewQuestionAnswerId: reply.reviewQuestionAnswerId,
                    user: reply.createdBy
                        ? (users.find((u) => u.id === reply.createdBy) ?? null)
                        : null,
                    text: reply.content,
                    timestamp: new Date(reply.createdAt),
                }),
            );

            const isNonCommentableQuestion = isNonCommentableQuestionType(
                questionAnswer.reviewQuestion.questionType,
            );

            const comment: QuestionComment | null =
                questionAnswer.comment && !isNonCommentableQuestion
                    ? {
                          id: questionAnswer.id ?? questionAnswer.reviewQuestionId,
                          reviewQuestionAnswerId: questionAnswer.id,
                          user: reviewerUser,
                          text: questionAnswer.comment,
                          timestamp: new Date(questionAnswer.updatedAt ?? new Date().toISOString()),
                          replies,
                      }
                    : null;

            if (existing) {
                existing.totalCount += 1;
                if (!questionAnswer.hasBeenSkipped) {
                    existing.answeredCount += 1;
                    aggregateAnswer(existing.answerData);
                    if (questionAnswer.textValue) {
                        existing.textAnswers.push({
                            user: reviewerUser,
                            text: questionAnswer.textValue,
                            submittedAt: new Date(
                                questionAnswer.updatedAt ?? new Date().toISOString(),
                            ),
                        });
                    }
                }
                if (comment) {
                    existing.comments.push(comment);
                }
            } else {
                const answerData: Record<number, number> = {};
                const textAnswers: TextAnswer[] = [];
                if (!questionAnswer.hasBeenSkipped) {
                    aggregateAnswer(answerData);
                    if (questionAnswer.textValue) {
                        textAnswers.push({
                            user: reviewerUser,
                            text: questionAnswer.textValue,
                            submittedAt: new Date(
                                questionAnswer.updatedAt ?? new Date().toISOString(),
                            ),
                        });
                    }
                }
                // Sort reviewQuestionOptions by sortOrder to ensure correct display order
                const sortedQuestion = {
                    ...questionAnswer.reviewQuestion,
                    reviewQuestionOptions: [
                        ...(questionAnswer.reviewQuestion.reviewQuestionOptions ?? []),
                    ].sort((a, b) => a.sortOrder - b.sortOrder),
                };

                questionMap.set(questionId, {
                    question: sortedQuestion,
                    categoryName: '',
                    averagePercentage: 0,
                    answeredCount: questionAnswer.hasBeenSkipped ? 0 : 1,
                    totalCount: 1,
                    answerData,
                    textAnswers,
                    comments: comment ? [comment] : [],
                });
            }
        }
    }

    return Array.from(questionMap.values()).map((data) => ({
        ...data,
        averagePercentage: data.totalCount > 0 ? (data.answeredCount / data.totalCount) * 100 : 0,
    }));
};
